import { LRU } from "./lru-cache"

export const cached = <Func extends AnyFunction, ID>(
	fn: Func,
	getId: (...args: Parameters<Func>) => ID,
) => {
	const cache = new Map<ID, ReturnType<Func>>()

	return (...args: Parameters<Func>): ReturnType<Func> => {
		const id = getId(...args)

		if (cache.has(id)) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return cache.get(id) as ReturnType<Func>
		}

		const res = fn(...args)

		cache.set(id, res)

		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return res
	}
}

export const cachedLRU = <Func extends (...args: any[]) => any, ID>(
	fn: Func,
	getId: (...args: Parameters<Func>) => ID,
	maxCacheSize = 100,
	evictionCallback: (value: Awaited<ReturnType<Func>>) => void = () => {},
) => {
	const cache = new LRU<ID, Awaited<ReturnType<Func>>>(maxCacheSize, evictionCallback)

	return (...args: Parameters<Func>): ReturnType<Func> => {
		const id = getId(...args)

		if (cache.has(id)) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return cache.get(id) as ReturnType<Func>
		}

		const res = fn(...args)

		// Handle both synchronous and asynchronous functions
		if (res instanceof Promise) {
			// For async functions
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return res.then((value) => {
				cache.set(id, value)
				// eslint-disable-next-line @typescript-eslint/no-unsafe-return
				return value
			}) as ReturnType<Func>
		} else {
			// For sync functions
			cache.set(id, res as Awaited<ReturnType<Func>>)
			// eslint-disable-next-line @typescript-eslint/no-unsafe-return
			return res
		}
	}
}
